import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { productosService } from '../../services/productosService';
import { empresasService } from '../../services/empresasService';
import { categoriasService } from '../../services/categoriasService';
import { sucursalesService } from '../../services/sucursalesService';

// Unidades de medida comunes (hardcodeadas por ahora)
const UNIDADES_MEDIDA = [
  { UnidadMedidaID: 1, Nombre: 'Pieza', Abreviatura: 'PZA' },
  { UnidadMedidaID: 2, Nombre: 'Caja', Abreviatura: 'CAJ' },
  { UnidadMedidaID: 3, Nombre: 'Kilogramo', Abreviatura: 'KG' },
  { UnidadMedidaID: 4, Nombre: 'Litro', Abreviatura: 'LT' },
  { UnidadMedidaID: 5, Nombre: 'Metro', Abreviatura: 'M' },
  { UnidadMedidaID: 6, Nombre: 'Par', Abreviatura: 'PAR' },
  { UnidadMedidaID: 7, Nombre: 'Docena', Abreviatura: 'DOC' },
];

function ProductoForm({ producto, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    empresaID: '',
    sku: '',
    nombre: '',
    codigoBarras: '',
    categoriaID: '',
    unidadMedidaID: '',
    costoCompra: 0,
    precioVentaGlobal: 0,
    stockMinimo: 0,
    activo: true,
    todasLasSucursales: true,
    sucursalID: null,
  });

  const [empresas, setEmpresas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (formData.empresaID) {
      loadCategorias();
      loadSucursales();
    } else {
      setCategorias([]);
      setSucursales([]);
    }
  }, [formData.empresaID]);

  useEffect(() => {
    if (producto) {
      const sucursalId = producto.SucursalID || producto.sucursalID;
      setFormData({
        empresaID: producto.EmpresaID || producto.empresaID || '',
        sku: producto.SKU || producto.sku || '',
        nombre: producto.Nombre || producto.nombre || '',
        codigoBarras: producto.CodigoBarras || producto.codigoBarras || '',
        categoriaID: producto.CategoriaID || producto.categoriaID || '',
        unidadMedidaID: producto.UnidadMedidaID || producto.unidadMedidaID || '',
        costoCompra: producto.CostoCompra || producto.costoCompra || 0,
        precioVentaGlobal: producto.PrecioVentaGlobal || producto.precioVentaGlobal || 0,
        stockMinimo: producto.StockMinimo !== undefined ? producto.StockMinimo : (producto.stockMinimo !== undefined ? producto.stockMinimo : 0),
        activo: producto.Activo !== undefined ? producto.Activo : (producto.activo !== undefined ? producto.activo : true),
        todasLasSucursales: !sucursalId,
        sucursalID: sucursalId || null,
      });
      if (producto.EmpresaID || producto.empresaID) {
        loadCategorias();
        loadSucursales();
      }
    }
  }, [producto]);

  const loadEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const normalized = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
        }));
        setEmpresas(normalized);
        if (normalized.length === 1 && (!formData.empresaID || formData.empresaID === '')) {
          setFormData(prev => ({ ...prev, empresaID: normalized[0].EmpresaID.toString() }));
        }
      }
    } catch (err) {
      setError('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const empresaId = parseInt(formData.empresaID);
      if (!empresaId) return;
      
      const response = await categoriasService.getAll(empresaId);
      if (response.success && response.data) {
        const categoriasData = Array.isArray(response.data) ? response.data : [];
        const normalized = categoriasData.map(cat => ({
          CategoriaID: cat.CategoriaID || cat.categoriaID,
          Nombre: cat.Nombre || cat.nombre || '',
        }));
        setCategorias(normalized);
      }
    } catch (err) {
    }
  };

  const loadSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresaId = parseInt(formData.empresaID);
      if (!empresaId) return;
      
      const response = await sucursalesService.getAll(empresaId);
      if (response.success && response.data) {
        const sucursalesData = Array.isArray(response.data) ? response.data : [];
        const normalized = sucursalesData.map(suc => ({
          SucursalID: suc.SucursalID || suc.sucursalID,
          Nombre: suc.Nombre || suc.nombre || '',
        }));
        setSucursales(normalized);
      }
    } catch (err) {
    } finally {
      setLoadingSucursales(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // EmpresaID es requerido
    if (!formData.empresaID) {
      newErrors.empresaID = 'La empresa es requerida';
    }

    // SKU es requerido
    if (!formData.sku.trim()) {
      newErrors.sku = 'El SKU es requerido';
    } else if (formData.sku.length > 50) {
      newErrors.sku = 'El SKU no puede exceder 50 caracteres';
    }

    // Nombre es requerido
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      newErrors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    // CodigoBarras opcional pero con límite
    if (formData.codigoBarras && formData.codigoBarras.length > 50) {
      newErrors.codigoBarras = 'El código de barras no puede exceder 50 caracteres';
    }

    // UnidadMedidaID es requerido
    if (!formData.unidadMedidaID) {
      newErrors.unidadMedidaID = 'La unidad de medida es requerida';
    }

    // CostoCompra debe ser >= 0
    if (formData.costoCompra < 0) {
      newErrors.costoCompra = 'El costo de compra no puede ser negativo';
    }

    // PrecioVentaGlobal debe ser > 0
    if (formData.precioVentaGlobal <= 0) {
      newErrors.precioVentaGlobal = 'El precio de venta global debe ser mayor a cero';
    }

    // StockMinimo debe ser >= 0
    if (formData.stockMinimo < 0) {
      newErrors.stockMinimo = 'El stock mínimo no puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const productoData = {
        EmpresaID: parseInt(formData.empresaID),
        SucursalID: formData.todasLasSucursales ? null : (formData.sucursalID || null),
        SKU: formData.sku.trim(),
        Nombre: formData.nombre.trim(),
        CodigoBarras: formData.codigoBarras?.trim() || null,
        CategoriaID: formData.categoriaID ? parseInt(formData.categoriaID) : null,
        UnidadMedidaID: parseInt(formData.unidadMedidaID),
        CostoCompra: parseFloat(formData.costoCompra) || 0,
        PrecioVentaGlobal: parseFloat(formData.precioVentaGlobal) || 0,
        StockMinimo: parseFloat(formData.stockMinimo) || 0,
      };

      let response;
      if (producto) {
        // Actualizar
        const productoId = producto.ProductoID || producto.productoID;
        productoData.ProductoID = productoId;
        productoData.Activo = formData.activo;
        response = await productosService.update(productoId, productoData);
      } else {
        // Crear
        response = await productosService.create(productoData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al guardar el producto');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Empresa <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="empresaID"
              value={formData.empresaID}
              onChange={handleChange}
              invalid={!!errors.empresaID}
              disabled={loading || loadingEmpresas || !!producto || empresas.length <= 1}
              required
            >
              <option value="">Seleccione una empresa</option>
              {empresas.map((emp) => (
                <option key={emp.EmpresaID} value={emp.EmpresaID}>
                  {emp.RazonSocial}
                </option>
              ))}
            </Input>
            {errors.empresaID && (
              <div className="text-danger small">{errors.empresaID}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Sucursales</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="todasLasSucursales"
                  checked={formData.todasLasSucursales}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      todasLasSucursales: e.target.checked,
                      sucursalID: e.target.checked ? null : prev.sucursalID,
                    }));
                  }}
                  disabled={!formData.empresaID || loadingSucursales}
                />
                <span className="form-check-sign">
                  <span className="check" />
                </span>
                Todas las sucursales
              </Label>
            </FormGroup>
            {!formData.todasLasSucursales && (
              <Input
                type="select"
                value={formData.sucursalID || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    sucursalID: e.target.value ? parseInt(e.target.value) : null,
                  }));
                }}
                disabled={!formData.empresaID || loadingSucursales}
              >
                <option value="">Seleccione una sucursal</option>
                {sucursales.map((suc) => (
                  <option key={suc.SucursalID} value={suc.SucursalID}>
                    {suc.Nombre}
                  </option>
                ))}
              </Input>
            )}
            {formData.empresaID && sucursales.length === 0 && !loadingSucursales && (
              <small className="form-text text-muted">
                No hay sucursales disponibles para esta empresa.
              </small>
            )}
            {loadingSucursales && (
              <small className="form-text text-muted">
                Cargando sucursales...
              </small>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              SKU <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              invalid={!!errors.sku}
              maxLength={50}
              required
            />
            {errors.sku && (
              <div className="text-danger small">{errors.sku}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Nombre <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              invalid={!!errors.nombre}
              maxLength={200}
              required
            />
            {errors.nombre && (
              <div className="text-danger small">{errors.nombre}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Código de Barras</Label>
            <Input
              type="text"
              name="codigoBarras"
              value={formData.codigoBarras}
              onChange={handleChange}
              invalid={!!errors.codigoBarras}
              maxLength={50}
            />
            {errors.codigoBarras && (
              <div className="text-danger small">{errors.codigoBarras}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Categoría</Label>
            <Input
              type="select"
              name="categoriaID"
              value={formData.categoriaID}
              onChange={handleChange}
              disabled={!formData.empresaID || loading}
            >
              <option value="">Sin categoría</option>
              {categorias.map((cat) => (
                <option key={cat.CategoriaID} value={cat.CategoriaID}>
                  {cat.Nombre}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>
              Unidad de Medida <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="unidadMedidaID"
              value={formData.unidadMedidaID}
              onChange={handleChange}
              invalid={!!errors.unidadMedidaID}
              required
            >
              <option value="">Seleccione una unidad</option>
              {UNIDADES_MEDIDA.map((um) => (
                <option key={um.UnidadMedidaID} value={um.UnidadMedidaID}>
                  {um.Nombre} ({um.Abreviatura})
                </option>
              ))}
            </Input>
            {errors.unidadMedidaID && (
              <div className="text-danger small">{errors.unidadMedidaID}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Costo de Compra <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              name="costoCompra"
              value={formData.costoCompra || ''}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
              invalid={!!errors.costoCompra}
              min="0"
              step="0.01"
              required
            />
            {errors.costoCompra && (
              <div className="text-danger small">{errors.costoCompra}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>
              Precio de Venta Global <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              name="precioVentaGlobal"
              value={formData.precioVentaGlobal || ''}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
              invalid={!!errors.precioVentaGlobal}
              min="0.01"
              step="0.01"
              required
            />
            {errors.precioVentaGlobal && (
              <div className="text-danger small">{errors.precioVentaGlobal}</div>
            )}
            <small className="form-text text-muted">
              Precio por defecto. Puede configurarse un precio específico por sucursal después de crear el producto.
            </small>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Stock Mínimo
            </Label>
            <Input
              type="number"
              name="stockMinimo"
              value={formData.stockMinimo || ''}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
              invalid={!!errors.stockMinimo}
              min="0"
              step="0.01"
            />
            {errors.stockMinimo && (
              <div className="text-danger small">{errors.stockMinimo}</div>
            )}
            <small className="form-text text-muted">
              Cantidad mínima de stock permitida. El sistema no permitirá ventas o movimientos que dejen el stock por debajo de este valor.
            </small>
          </FormGroup>
        </Col>
      </Row>

      {producto && (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
            Producto Activo
          </Label>
        </FormGroup>
      )}

      <div className="text-right mt-4">
        <Button color="secondary" onClick={onCancel} className="mr-2">
          Cancelar
        </Button>
        <Button color="primary" type="submit" disabled={loading || loadingEmpresas}>
          {loading ? 'Guardando...' : producto ? 'Actualizar' : 'Crear'}
        </Button>
      </div>
    </Form>
  );
}

export default ProductoForm;

